import { Component, OnInit, OnDestroy, EventEmitter, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TabSdk } from '@techextensor/tab-sdk';
import { TreeNode, ObjectTypeFilter } from '../../../../../core/interfaces/dev-tool/db-viewer.interface';
import { ContextMenuModule, ContextMenuComponent, MenuItemModel, MenuEventArgs } from '@syncfusion/ej2-angular-navigations';

@Component({
  selector: 'app-schema-explorer',
  standalone: true,
  imports: [CommonModule, FormsModule, ContextMenuModule],
  templateUrl: './schema-explorer.component.html',
  styleUrl: './schema-explorer.component.scss'
})
export class SchemaExplorerComponent implements OnInit, OnDestroy {
  @Output() fieldDragStart = new EventEmitter<{ tableName: string; fieldName: string; qualifiedName: string }>();
  @Output() tableDragStart = new EventEmitter<{ tableName: string; tableDisplayName: string }>();
  @Output() generateQuery = new EventEmitter<{ query: string; execute: boolean }>();

  @ViewChild('contextMenu') contextMenu?: ContextMenuComponent;

  searchTerm: string = '';
  treeNodes: TreeNode[] = [];
  isLoading: boolean = false;
  objectTypeFilter: ObjectTypeFilter = ObjectTypeFilter.All; // Filter state

  // Expose enum to template
  readonly ObjectTypeFilter = ObjectTypeFilter;

  // Reusable drag image element (created once, reused for all drags)
  private dragImageElement?: HTMLElement;

  // Store all nodes before filtering
  private allTreeNodes: TreeNode[] = [];

  // Cached data type constants (no need to recreate on every call)
  private readonly DATA_TYPE_MAP: { [key: number]: string } = {
    2: 'Int',
    3: 'Boolean',
    4: 'Decimal',
    5: 'Date',
    6: 'Timestamp',
    13: 'String',
    24: 'String',
    17: 'Lookup'
  };

  // Cached parent node map for O(1) lookups
  private _parentNodeMap: Map<string, TreeNode> = new Map();
  private _cacheInvalidated: boolean = true;

  // Context menu
  private contextMenuTarget: { node: TreeNode; isTable: boolean } | null = null;

  // Context menu items
  menuItems: MenuItemModel[] = [
    {
      text: 'Select All Rows',
      iconCss: 'e-icons e-table',
      id: 'select-all'
    },
    {
      separator: true
    },
    {
      text: 'Copy',
      iconCss: 'e-icons e-copy',
      id: 'copy'
    }
  ];

  constructor() {}

  /**
   * Update parent node map cache for fast lookups
   */
  private updateCache(): void {
    this._parentNodeMap.clear();
    for (const node of this.treeNodes) {
      if (node.children) {
        for (const child of node.children) {
          this._parentNodeMap.set(child.id, node);
        }
      }
    }
    this._cacheInvalidated = false;
  }

  /**
   * Invalidate cache when tree structure changes
   */
  private invalidateCache(): void {
    this._cacheInvalidated = true;
  }

  ngOnInit(): void {
    this.loadSchema();
  }

  loadSchema(): void {
    this.isLoading = true;
    try {
      // Get all app objects from TabSdk metadata utils
      const appObjects = TabSdk.metadataUtils.getAllAppObjects();
      this.allTreeNodes = this.buildTreeNodes(appObjects);
      this.applyFilters();
      this.invalidateCache();
      this.isLoading = false;
    } catch (error) {
      console.error('Error loading schema:', error);
      this.isLoading = false;
    }
  }

  /**
   * Apply search and object type filters
   */
  applyFilters(): void {
    let filtered = [...this.allTreeNodes];

    // Apply object type filter
    if (this.objectTypeFilter === ObjectTypeFilter.Table) {
      filtered = filtered.filter(node => node.objectType !== 2); // Exclude views (type 2)
    } else if (this.objectTypeFilter === ObjectTypeFilter.View) {
      filtered = filtered.filter(node => node.objectType === 2); // Only views
    }

    // Apply search filter
    if (this.searchTerm.trim()) {
      const term = this.searchTerm.toLowerCase();
      filtered = filtered.filter(node =>
        node.name.toLowerCase().includes(term) ||
        node.displayName.toLowerCase().includes(term)
      );
    }

    this.treeNodes = filtered;
    this.invalidateCache();
  }

  /**
   * Set object type filter
   */
  setObjectTypeFilter(filter: ObjectTypeFilter): void {
    this.objectTypeFilter = filter;
    this.applyFilters();
  }

  buildTreeNodes(appObjects: any[]): TreeNode[] {
    return appObjects.map(appObject => {
      const fields = appObject.fields();

      return {
        id: appObject.ID,
        name: appObject.ObjectName,
        displayName: appObject.DisplayName,
        type: 'object' as const,
        icon: '📊',
        isExpanded: false,
        objectType: appObject.ObjectType,
        fieldCount: fields.length,
        children: fields.map((field: any) => {
          const dataType = this.getDataTypeName(field.FieldType?.DataType);
          const isPrimaryKey = field.IsPrimaryKey;
          const isRequired = field.IsRequired;
          const isLookup = field.FieldType?.DataType === 17;
          const lookupTarget = isLookup && field.LookUpDetails ?
            `${field.LookUpDetails.LookupObject}.${field.LookUpDetails.LookupField}` : undefined;

          // Pre-compute tooltip to avoid recalculation on every change detection
          const tooltip = this.computeFieldTooltip(dataType, isPrimaryKey, isRequired, isLookup, lookupTarget);

          return {
            id: field.ID,
            name: field.FieldName,
            displayName: field.DisplayName,
            type: 'field' as const,
            icon: this.getFieldIcon(field.FieldType?.DataType, isPrimaryKey, isLookup),
            dataType,
            isExpanded: false,
            isPrimaryKey,
            isRequired,
            isLookup,
            lookupTarget,
            tooltip, // Pre-computed tooltip
            fieldData: field
          };
        })
      };
    });
  }

  getFieldIcon(dataType: number, isPrimaryKey: boolean, isLookup: boolean): string {
    if (isPrimaryKey) return '🔑';
    if (isLookup) return '🔗';

    switch (dataType) {
      case 13: // String
      case 24: // Text
        return '📝';
      case 2: // Number
      case 4: // Decimal
        return '#️⃣';
      case 5: // DateTime
      case 6: // TimeStamp
        return '📅';
      case 3: // Boolean
        return '✓';
      default:
        return '📋';
    }
  }

  /**
   * Get data type name from numeric type - uses cached map for performance
   */
  getDataTypeName(dataType: number): string {
    return this.DATA_TYPE_MAP[dataType] || 'Unknown';
  }

  toggleNode(node: TreeNode): void {
    if (node.type === 'object') {
      node.isExpanded = !node.isExpanded;
    }
  }

  searchSchema(): void {
    this.applyFilters();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.loadSchema();
  }

  onTableDragStart(event: DragEvent, table: TreeNode): void {
    if (!event.dataTransfer) return;

    // Emit to parent component
    this.tableDragStart.emit({
      tableName: table.name,
      tableDisplayName: table.displayName
    });

    // Set drag data - use table name with prefix to distinguish from fields
    event.dataTransfer.setData('text/plain', `TABLE:${table.name}`);
    event.dataTransfer.effectAllowed = 'copy';

    // Set custom drag image
    this.setDragImage(event, table.displayName, '', true);
  }

  onTableDragEnd(event: DragEvent): void {
    this.resetDragStyles(event);
  }

  onFieldDragStart(event: DragEvent, field: TreeNode): void {
    if (!event.dataTransfer || !field.fieldData) return;

    const parentNode = this.findParentNode(field.id);
    const qualifiedName = parentNode
      ? `${parentNode.name}.${field.name}`
      : field.name;

    // Emit to parent component
    this.fieldDragStart.emit({
      tableName: parentNode?.name || '',
      fieldName: field.name,
      qualifiedName
    });

    // Set drag data - use only field name
    event.dataTransfer.setData('text/plain', field.name);
    event.dataTransfer.effectAllowed = 'copy';

    // Set custom drag image
    this.setDragImage(event, field.displayName, field.icon || '📋', false);
  }

  onFieldDragEnd(event: DragEvent): void {
    this.resetDragStyles(event);
  }

  /**
   * Set custom drag image and apply visual feedback
   * Optimized: reuses drag image element instead of creating new ones
   */
  private setDragImage(event: DragEvent, text: string, icon: string, isTable: boolean): void {
    if (!event.dataTransfer) return;

    // Update reusable drag image element
    this.updateDragImage(text, icon, isTable);

    if (this.dragImageElement) {
      event.dataTransfer.setDragImage(this.dragImageElement, 0, 0);
    }

    // Visual feedback on source element
    const target = event.currentTarget as HTMLElement;
    if (target) {
      target.style.opacity = '0.5';
    }
  }

  /**
   * Reset drag visual feedback
   */
  private resetDragStyles(event: DragEvent): void {
    const target = event.currentTarget as HTMLElement;
    if (target) {
      target.style.opacity = '1';
    }
  }

  /**
   * Find parent node for a field - optimized with Map lookup O(1)
   */
  findParentNode(fieldId: string): TreeNode | undefined {
    if (this._cacheInvalidated) {
      this.updateCache();
    }
    return this._parentNodeMap.get(fieldId);
  }

  /**
   * Compute field tooltip from field properties
   * Called during tree building to pre-compute tooltips
   */
  private computeFieldTooltip(
    dataType: string,
    isPrimaryKey: boolean,
    isRequired: boolean,
    isLookup: boolean,
    lookupTarget?: string
  ): string {
    const parts = [
      `Type: ${dataType}`,
      isPrimaryKey ? 'Primary Key' : null,
      isRequired ? 'Required' : 'Optional',
      isLookup && lookupTarget ? `→ ${lookupTarget}` : null
    ].filter(Boolean);

    return parts.join(' • ');
  }

  /**
   * Get field tooltip - returns pre-computed value for performance
   */
  getFieldTooltip(field: TreeNode): string {
    return field.tooltip || '';
  }

  // ==================== CONTEXT MENU ====================

  /**
   * Handle right-click on table node
   */
  onTableContextMenu(event: MouseEvent, node: TreeNode): void {
    event.preventDefault();
    event.stopPropagation();

    this.contextMenuTarget = { node, isTable: true };

    if (this.contextMenu) {
      this.contextMenu.open(event.clientY, event.clientX, event.target as HTMLElement);
    }
  }

  /**
   * Handle right-click on field node
   */
  onFieldContextMenu(event: MouseEvent, field: TreeNode, parentNode: TreeNode): void {
    event.preventDefault();
    event.stopPropagation();

    this.contextMenuTarget = { node: field, isTable: false };

    if (this.contextMenu) {
      this.contextMenu.open(event.clientY, event.clientX, event.target as HTMLElement);
    }
  }

  /**
   * Handle context menu item selection
   */
  onContextMenuSelect(args: MenuEventArgs): void {
    if (!this.contextMenuTarget) return;

    const { node, isTable } = this.contextMenuTarget;
    const menuId = args.item.id;

    switch (menuId) {
      case 'select-all':
        this.handleSelectAll(node, isTable);
        break;
      case 'copy':
        this.handleCopy(node, isTable);
        break;
    }

    this.contextMenuTarget = null;
  }

  /**
   * Generate SELECT ALL query
   */
  private handleSelectAll(node: TreeNode, isTable: boolean): void {
    const tableName = isTable ? node.name : this.getParentTableName(node);
    if (!tableName) return;
    const query = `SELECT * FROM ${tableName}`;
    this.generateQuery.emit({ query, execute: true });
  }

  /**
   * Copy table or field name to clipboard
   */
  private handleCopy(node: TreeNode, isTable: boolean): void {
    const textToCopy = isTable ? node.name : node.name;
    TabSdk.ui.copyToClipboard(textToCopy);
  }

  /**
   * Get parent table name for a field
   */
  private getParentTableName(fieldNode: TreeNode): string | null {
    if (this._cacheInvalidated) {
      this.updateCache();
    }
    const parent = this._parentNodeMap.get(fieldNode.id);
    return parent?.name || null;
  }

  /**
   * Create or get the reusable drag image element
   * Optimized: creates element once and reuses it
   */
  private getDragImageElement(): HTMLElement {
    if (!this.dragImageElement) {
      const dragEl = document.createElement('div');
      dragEl.className = 'drag-image-preview';

      // Base styles
      Object.assign(dragEl.style, {
        position: 'absolute',
        top: '-1000px',
        left: '-1000px',
        padding: '8px 12px',
        background: '#ffffff',
        color: '#1f2937',
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        fontSize: '13px',
        fontWeight: '500',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        maxWidth: '250px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        pointerEvents: 'none'
      });

      document.body.appendChild(dragEl);
      this.dragImageElement = dragEl;
    }
    return this.dragImageElement;
  }

  /**
   * Update the drag image element content
   * Reuses existing element for better performance
   */
  private updateDragImage(text: string, icon: string, isTable: boolean): void {
    const dragEl = this.getDragImageElement();

    // Update content based on type
    if (isTable) {
      dragEl.innerHTML = `<span>${this.escapeHtml(text)}</span>`;
    } else {
      dragEl.innerHTML = `<span style="font-size: 14px;">${icon}</span><span>${this.escapeHtml(text)}</span>`;
    }
  }

  /**
   * Escape HTML to prevent XSS
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Clean up drag image element on component destroy
   */
  ngOnDestroy(): void {
    if (this.dragImageElement && this.dragImageElement.parentNode) {
      this.dragImageElement.parentNode.removeChild(this.dragImageElement);
      this.dragImageElement = undefined;
    }
  }
}
